import React, { useState, useEffect, useCallback } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import useSetupStatus from './hooks/useSetupStatus';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { SettingsProvider } from './contexts/SettingsContext';
import { AuthProvider } from './contexts/AuthContext';
import { Box, CircularProgress, Typography, Container, Avatar } from '@mui/material';
import config from './config';

// Composants de pages
import Layout from './components/layout/Layout';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import Dashboard from './pages/dashboard/Dashboard';
import Clients from './pages/clients/Clients';
import Reservations from './pages/reservations/Reservations';
import Forfaits from './pages/forfaits/Forfaits';
import FinancesPage from './pages/finances/FinancesPage';
import InvoicingPage from './pages/invoicing/InvoicingPage';
import Fournisseurs from './pages/fournisseurs/Fournisseurs';
import Calendrier from './pages/calendrier';
import Documents from './pages/documents/Documents';
import Rapports from './pages/rapports/Rapports';
import Parametres from './pages/parametres/Parametres';
import ParametresEtendu from './pages/parametres/ParametresEtendu';
// import InvoitLicensingDashboard from './pages/licensing/InvoitLicensingDashboard'; // Removed for demo
import TravelITSetupWizard from './pages/setup/TravelITSetupWizard';
import TravelITLogin from './pages/auth/TravelITLogin';
// Composants de démonstration supprimés
import ClientsReports from './pages/clients/ClientsReports';
import FinancesReports from './pages/finances/FinancesReports';
import ReservationsReports from './pages/reservations/ReservationsReports';
import NotFound from './pages/NotFound';

// Thème moderne et professionnel
let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#1e40af', // Modern blue
      light: '#3b82f6',
      dark: '#1e3a8a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed', // Modern purple
      light: '#8b5cf6',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Very light gray
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: { xs: '1.375rem', sm: '1.425rem', md: '1.5rem' },
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: { xs: '1.125rem', sm: '1.2rem', md: '1.25rem' },
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: { xs: '1rem', sm: '1.05rem', md: '1.125rem' },
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: { xs: '0.875rem', sm: '0.925rem', md: '1rem' },
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: { xs: '0.9375rem', md: '1rem' },
      lineHeight: 1.6,
    },
    body2: {
      fontSize: { xs: '0.8125rem', md: '0.875rem' },
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: { xs: '0.875rem', md: '0.9375rem' },
    },
    caption: {
      fontSize: { xs: '0.7rem', md: '0.75rem' },
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: { xs: '0.9375rem', md: '1rem' },
      lineHeight: 1.5,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: { xs: '0.8125rem', md: '0.875rem' },
      lineHeight: 1.5,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  ],
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: { xs: '12px', sm: '8px' }, // Larger touch target on mobile
          '&.MuiIconButton-sizeSmall': {
            padding: { xs: '8px', sm: '4px' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: { xs: '10px 16px', md: '8px 16px' },
          boxShadow: 'none',
          minHeight: { xs: '44px', md: 'inherit' }, // Larger touch target on mobile
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          '@media (hover: none)': {
            '&:hover': {
              boxShadow: 'none',
              transform: 'none',
            },
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
        },
        // Ensure proper spacing between buttons on mobile
        sizeLarge: {
          padding: { xs: '12px 20px', md: '10px 22px' },
          fontSize: { xs: '0.9375rem', md: '1rem' },
          minHeight: { xs: '48px', md: 'inherit' },
        },
        sizeSmall: {
          padding: { xs: '8px 12px', md: '4px 10px' },
          minHeight: { xs: '38px', md: 'inherit' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: { xs: 12, md: 16 },
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          },
          // Optimize for mobile screens
          margin: { xs: '0 0 16px 0', md: 'inherit' },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: { xs: '16px', sm: '20px', md: '24px' },
          '&:last-child': {
            paddingBottom: { xs: '16px', sm: '20px', md: '24px' },
          },
          // Better spacing on mobile
          '& > *:not(:last-child)': {
            marginBottom: { xs: '12px', md: '16px' },
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: { xs: '16px', sm: '16px 20px', md: '20px 24px' },
        },
        title: {
          fontSize: { xs: '1rem', md: '1.125rem' },
        },
        subheader: {
          fontSize: { xs: '0.75rem', md: '0.875rem' },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: { xs: '8px 16px 16px', md: '8px 24px 24px' },
          '& .MuiButton-root': {
            marginLeft: { xs: '4px', md: '8px' },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: { xs: 12, md: 16 },
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: '#1e40af',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1e3a8a',
            },
            '& .MuiListItemIcon-root': {
              color: 'white',
            },
          },
          '&:hover': {
            backgroundColor: '#f1f5f9',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&.MuiChip-outlined': {
            borderWidth: '1px',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0 8px',
          // Make tables responsive with horizontal scrolling on mobile
          display: { xs: 'block', md: 'table' },
          width: '100%',
          overflowX: { xs: 'auto', md: 'visible' },
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '3px',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
          // Improve mobile display
          display: { xs: 'block', md: 'table-row' },
          marginBottom: { xs: '0px', md: '0' },
          '& > *:not(:last-child)': {
            borderBottom: { xs: '0', md: 'inherit' },
          },
        },
        head: {
          display: { xs: 'none', sm: 'table-row' }, // Hide header row on very small screens
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: { xs: '12px', sm: '14px', md: '16px' },
          borderBottom: 'none',
          '&:first-of-type': {
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            paddingLeft: { xs: '16px', md: '16px' },
          },
          '&:last-of-type': {
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            paddingRight: { xs: '16px', md: '16px' },
          },
          // Responsive adjustments
          display: { xs: 'block', md: 'table-cell' },
          textAlign: { xs: 'left', md: 'inherit' },
          minHeight: { xs: '24px', md: 'inherit' },
          position: 'relative',
          // Add data labels for mobile view
          '&[data-label]::before': {
            content: 'attr(data-label)',
            display: { xs: 'inline-block', md: 'none' },
            fontWeight: 600,
            marginRight: '8px',
            minWidth: '120px',
            color: '#64748b',
          },
        },
        head: {
          backgroundColor: 'transparent',
          fontWeight: 600,
          color: '#64748b',
          fontSize: { xs: '0.75rem', md: '0.875rem' },
          lineHeight: { xs: 1.3, md: 1.5 },
          whiteSpace: 'nowrap',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        },
        body: {
          backgroundColor: 'white',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05)',
          border: '1px solid #f1f5f9',
          fontSize: { xs: '0.875rem', md: '0.9375rem' },
          // Improve readability on mobile
          whiteSpace: { xs: 'normal', md: 'nowrap' },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& input': {
              padding: { xs: '14px 16px', md: '16.5px 14px' }, // Taller input on mobile
              fontSize: { xs: '16px', md: 'inherit' }, // Prevent zoom on iOS
            },
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3b82f6',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.9375rem', md: '1rem' },
          },
          '& .MuiInputAdornment-root': {
            '& .MuiIconButton-root': {
              padding: { xs: '8px', md: '8px' }, // Larger touch target for icons
            },
          },
          marginBottom: { xs: '16px', md: '8px' }, // More spacing between fields on mobile
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          // Ensure Grid items stack properly on mobile
          '& .MuiGrid-item': {
            width: '100%',
          },
          // Adjust spacing for mobile
          '&.MuiGrid-container': {
            marginTop: { xs: '-8px', sm: '-12px', md: '-16px' },
            marginLeft: { xs: '-8px', sm: '-12px', md: '-16px' },
            width: { xs: 'calc(100% + 8px)', sm: 'calc(100% + 12px)', md: 'calc(100% + 16px)' },
            '& > .MuiGrid-item': {
              paddingTop: { xs: '8px', sm: '12px', md: '16px' },
              paddingLeft: { xs: '8px', sm: '12px', md: '16px' },
            },
          },
          // Adjust spacing for nested grids
          '& .MuiGrid-container': {
            marginTop: { xs: '-4px', sm: '-8px', md: '-8px' },
            marginLeft: { xs: '-4px', sm: '-8px', md: '-8px' },
            width: { xs: 'calc(100% + 4px)', sm: 'calc(100% + 8px)', md: 'calc(100% + 8px)' },
            '& > .MuiGrid-item': {
              paddingTop: { xs: '4px', sm: '8px', md: '8px' },
              paddingLeft: { xs: '4px', sm: '8px', md: '8px' },
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            // Make tabs responsive on mobile
            minWidth: 'auto',
            padding: '12px',
            '@media (max-width:600px)': {
              padding: '8px',
              fontSize: '0.85rem',
            },
          }
        }
      }
    }
  },
});

// Apply responsive fonts
theme = responsiveFontSizes(theme);



// Loading Component
const LoadingScreen = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh', 
      bgcolor: '#f8fafc' 
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Typography 
        variant="h3" 
        component="div" 
        sx={{ 
          fontWeight: 700,
          color: '#1e40af',
          letterSpacing: '-0.5px'
        }}
      >
        TravelIT
      </Typography>
    </Box>
    <CircularProgress color="primary" size={50} />
    <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: '#1e40af' }}>
      Chargement de l'application...
    </Typography>
  </Box>
);

// Route Guard Component to prevent blank pages
const RouteGuard = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isSetupComplete, isLoading: setupLoading } = useSetupStatus();
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleNavigation = useCallback(() => {
    // DEMO MODE: Bypass authentication - always allow access to dashboard
    console.log('🎭 DEMO MODE: Bypassing authentication checks');
    
    // Redirect login/setup/root to dashboard for demo
    if (location.pathname === '/login' || location.pathname === '/setup' || location.pathname === '/') {
      console.log('🎯 DEMO MODE: Redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }
    
    console.log('✅ DEMO MODE: Access allowed to', location.pathname);
      
  }, [location.pathname, navigate]);
  
  // Handle navigation on every change
  useEffect(() => {
    handleNavigation();
  }, [handleNavigation, isAuthenticated, isLoading, isSetupComplete, setupLoading, location.pathname]);
  
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Toaster position="top-right" />
          <I18nextProvider i18n={i18n}>
            <AuthProvider>
              <SettingsProvider>
                <Router>
                  <RouteGuard />
                  <Routes>
                    {/* Setup wizard route - outside of Layout */}
                    <Route path="/setup" element={<TravelITSetupWizard />} />
                    
                    {/* Login route - outside of Layout */}
                    <Route path="/login" element={<TravelITLogin />} />
                    
                    {/* Main application routes - protected by RouteGuard */}
                    <Route path="/" element={<Layout />}>
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="tableau-de-bord" element={<Dashboard />} />
                      
                      <Route path="clients">
                        <Route index element={<Clients />} />
                        <Route path="rapports" element={<ClientsReports />} />
                        <Route path="reports" element={<ClientsReports />} />
                      </Route>
                      
                      <Route path="reservations">
                        <Route index element={<Reservations />} />
                        <Route path="rapports" element={<ReservationsReports />} />
                      </Route>
                      
                      <Route path="forfaits">
                        <Route index element={<Forfaits />} />
                      </Route>
                      
                      <Route path="finances">
                        <Route index element={<FinancesPage />} />
                        <Route path="rapports" element={<FinancesReports />} />
                      </Route>
                      
                      <Route path="invoicing/*" element={<InvoicingPage />} />
                      <Route path="fournisseurs" element={<Fournisseurs />} />
                      <Route path="calendrier" element={<Calendrier />} />
                      <Route path="documents" element={<Documents />} />
                      
                      <Route path="rapports" element={<Rapports />} />
                      
                      <Route path="parametres">
                        <Route index element={<Parametres />} />
                        <Route path="etendu" element={<ParametresEtendu />} />
                      </Route>
                      
                      {/* Licensing routes removed for demo access */}
                      {/* <Route path="licensing">
                        <Route index element={<InvoitLicensingDashboard />} />
                        <Route path="dashboard" element={<InvoitLicensingDashboard />} />
                      </Route> */}
                      
                      {/* Fallback for authenticated users */}
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                    
                    {/* Fallback routes */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Router>
              </SettingsProvider>
            </AuthProvider>
          </I18nextProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App; 